import { useCallback, useEffect, useMemo } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";

import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowPinned,
} from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import { FormField } from "src/cases/caseView/components/FormField";
import { FormFieldLabel } from "src/cases/caseView/components/FormFieldLabel";
import {
  type CaseTask,
  TaskCheckbox,
} from "src/cases/caseView/components/TaskCheckbox";
import {
  useCaseActionFormSchema,
  useCaseTaskFormSchema,
  useGetCaseTemplateAction,
  useGetCaseTemplateTask,
} from "src/cases/caseView/hooks";
import { useCase } from "src/cases/queries/cases";
import { CaseDeserialized } from "src/cases/queries/casesTypes";

type FloatingFormTitleProps = {
  title: string;
  description?: string | null;
  task: CaseTask;
  caseData?: CaseDeserialized;
  disabled?: boolean;
};

const FloatingFormTitle: React.FC<FloatingFormTitleProps> = ({
  title,
  description,
  task,
  caseData,
  disabled,
}) => (
  <div className="flex min-w-0 flex-col gap-y-1" data-grabbable>
    <div className="flex min-w-0 gap-x-2" data-grabbable>
      <div className="pt-0.5">
        <TaskCheckbox caseData={caseData} disabled={disabled} task={task} />
      </div>
      <div className="min-w-0" data-grabbable>
        <p className="truncate font-inter-semibold-13px text-gray-800">
          {title}
        </p>
        {description && (
          <MarkdownRenderer content={description} variant="muted" />
        )}
      </div>
    </div>
  </div>
);

type HistoryFormData = {
  formType: "task" | "action";
  key: string;
  displayName: string;
  formData: Record<string, unknown>;
  caseId: string;
  description?: string;
};

export const HistoryFormViewWindow: React.FC<FloatingWindowProps> = ({
  id,
  computePosition,
  data,
}) => {
  const { formType, key, displayName, formData, caseId, description } =
    data as HistoryFormData;

  const { close, setPin } = useFloatingWindowsActions();
  const isPinned = useIsFloatingWindowPinned(id);
  const { position, windowRef } = useFloatingWindowPosition(computePosition);

  const { data: caseData } = useCase(caseId);
  const getTemplateTask = useGetCaseTemplateTask(caseData);
  const getTemplateAction = useGetCaseTemplateAction(caseData);

  const isTask = formType === "task";
  const templateTask = isTask ? getTemplateTask(key) : undefined;
  const templateAction = isTask ? undefined : getTemplateAction(key);

  const fields = isTask
    ? (templateTask?.form?.fields ?? [])
    : (templateAction?.form?.fields ?? []);

  const taskSchema = useCaseTaskFormSchema(templateTask);
  const actionSchema = useCaseActionFormSchema(templateAction);
  const schema = isTask ? taskSchema : actionSchema;

  const form = useForm<Record<string, unknown>>({
    mode: "onChange",
    defaultValues: formData,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    form.reset(formData);
  }, [formData, form]);

  const closeWindow = useCallback(() => close(id), [close, id]);
  const togglePin = useCallback(
    () => setPin(id, !isPinned),
    [setPin, id, isPinned],
  );

  const formTitle = useMemo(
    () => (
      <FloatingFormTitle
        description={description}
        task={{
          id: "",
          key,
          title: displayName,
          isCompleted: true,
        }}
        title={displayName}
        disabled
      />
    ),
    [key, displayName, description],
  );

  if (fields.length === 0) {
    return null;
  }

  return (
    <FloatingWindowInternal
      ref={windowRef}
      clipOverflow={false}
      headerAlignment="start"
      height="h-96"
      id={id}
      isPinned={isPinned}
      maximizable={false}
      style={{
        inset: "0px auto auto 0px",
        transform: position,
      }}
      title={formTitle}
      draggable
      resizeable
      onClose={closeWindow}
      onPin={togglePin}
    >
      <div className="relative -mx-2 flex h-full flex-col">
        <div className="decideScrollbar p-4">
          <FormProvider {...form}>
            <form className="flex flex-col gap-y-3">
              {fields.map((field) => (
                <div key={field.key}>
                  <FormFieldLabel field={field} />
                  <FormField field={field} disabled />
                </div>
              ))}
            </form>
          </FormProvider>
        </div>
      </div>
    </FloatingWindowInternal>
  );
};
